import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveContext } from '@/auth/ActiveContextProvider';
import type { Profile } from '@/auth/OperatorProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient } from '@/lib/api-client';
import { readErrorKey } from '@/lib/read-error';
import { CommunicationsSection } from '@/communications/profile/CommunicationsSection';
import { ContextsSection } from './ContextsSection';
import { DisplayNameForm } from './DisplayNameForm';
import { LanguageSection } from './LanguageSection';
import { MembershipsSection } from './MembershipsSection';
import { OwnerDetailsSection, type OwnerDetails } from './OwnerDetailsSection';
import { RoleAssignmentsSection } from './RoleAssignmentsSection';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; httpStatus: number | undefined }
  | { status: 'ready'; profile: Profile };

/**
 * The self-service profile section (FR-SP-1 through FR-SP-4; AC-PA-7, AC-PA-8, AC-PA-9's
 * UI-observable half), reachable at `/profile` by EVERY authenticated User — unlike the operator
 * admin section (`/admin/*`), this route carries no `AdminGate`/`useIsOperator()` check
 * (Resolution 2). Self-only by construction: `GET`/`PATCH /v1/profile` (SL-0022) accept no
 * target-user parameter anywhere, so this page can only ever render or edit the CALLER'S OWN
 * profile. Renders the caller's own identity, current/eligible active context, role assignments,
 * and Department/Group memberships as READ-ONLY cards/tables (`ContextsSection`,
 * `RoleAssignmentsSection`, `MembershipsSection`, Resolution 5); the editable controls are the
 * display name (`DisplayNameForm`), the language, and — after the Identity card — the caller's own
 * tax code and address for Note spese's PDF (`OwnerDetailsSection`, R-c); no role, scope,
 * membership, or entitlement editing control exists here (Resolution 7). Passes the caller's OWN current
 * active-context header pair explicitly (derived from `useActiveContext()`, deterministic — never
 * the ambient `api-client.ts` store) so the returned `activeContext` field reliably echoes the
 * caller's own current selection (Resolution 4).
 */
export function ProfilePage() {
  const { t } = useTranslation(['profile', 'common', 'errors']);
  const { status: activeContextStatus, active } = useActiveContext();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = () => {
    if (activeContextStatus === 'loading') {
      return;
    }
    setState({ status: 'loading' });
    const request =
      active !== null
        ? apiClient.GET('/v1/profile', {
            params: {
              header: {
                'X-Active-Tenant-Id': active.tenantId,
                'X-Active-Organization-Id': active.organizationId,
              },
            },
          })
        : apiClient.GET('/v1/profile');

    request
      .then(({ data, error, response }) => {
        // The status chooses the words (`readErrorKey`); it is never shown itself.
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error', httpStatus: response.status });
          return;
        }
        setState({ status: 'ready', profile: data });
      })
      .catch(() => setState({ status: 'error', httpStatus: undefined }));
  };

  useEffect(load, [activeContextStatus, active]);

  const handleDisplayNameUpdated = (updated: { displayName: string }) => {
    setState((previous) =>
      previous.status === 'ready'
        ? { status: 'ready', profile: { ...previous.profile, displayName: updated.displayName } }
        : previous,
    );
  };

  const handleOwnerDetailsSaved = (ownerDetails: OwnerDetails | null) => {
    setState((previous) =>
      previous.status === 'ready'
        ? { status: 'ready', profile: { ...previous.profile, ownerDetails } }
        : previous,
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="screen-title">{t('title')}</h2>

      {state.status === 'loading' && (
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <div className="bg-muted/60 h-24 animate-pulse rounded-lg" />
          <div className="bg-muted/40 h-24 animate-pulse rounded-lg" />
        </div>
      )}
      {state.status === 'error' && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 p-6">
            <CircleAlert className="text-destructive size-6 shrink-0" aria-hidden="true" />
            <p className="text-sm">{t(readErrorKey(state.httpStatus))}</p>
            <Button variant="outline" size="sm" className="ml-auto" onClick={load}>
              {t('common:retry')}
            </Button>
          </CardContent>
        </Card>
      )}
      {state.status === 'ready' && (
        <>
          {/* One Identity card: read-only email + user id, and the display name as the single
              editable field (label once) — the previous design repeated "display name" three
              times across a read-only line, a separate edit card's heading, and its field label. */}
          <Card>
            <CardHeader>
              <CardTitle className="card-label">{t('identity.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <dl className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-1">
                  <dt className="field-label">{t('identity.email')}</dt>
                  <dd className="text-sm">{state.profile.email}</dd>
                </div>
                <div className="space-y-1">
                  <dt className="field-label">{t('identity.userId')}</dt>
                  <dd className="text-foreground/80 font-mono text-xs break-all">
                    {state.profile.id}
                  </dd>
                </div>
              </dl>
              <div className="border-border border-t pt-5">
                <DisplayNameForm
                  currentDisplayName={state.profile.displayName}
                  onUpdated={handleDisplayNameUpdated}
                />
              </div>
            </CardContent>
          </Card>

          <OwnerDetailsSection
            // An older core-api, mid-rollout, answers without the field.
            ownerDetails={state.profile.ownerDetails ?? null}
            onSaved={handleOwnerDetailsSaved}
          />

          <LanguageSection />

          <CommunicationsSection />

          <ContextsSection
            activeContext={state.profile.activeContext}
            eligibleContexts={state.profile.eligibleContexts}
          />

          <RoleAssignmentsSection roleAssignments={state.profile.roleAssignments} />

          <MembershipsSection memberships={state.profile.memberships} />
        </>
      )}
    </div>
  );
}
